<script>
	/**
	 * [화면] 탭 — 메인(홈) · 온보딩 · 로그인 · 개인정보 안내.
	 * 설계: project-le868/launcher-page-design.md §2-1 · §2-4
	 *
	 * ⭐ **메인 화면이 여기 맨 앞에 있다.** 예전에는 [디자인] 탭에 있었는데, 프리뷰 화면을 가리키는
	 *    건 이 탭뿐이라 **만드는 걸 보면서 만들 수가 없었다**. 화면은 화면끼리 모아 둔다.
	 * ⭐ 온보딩·로그인·안내는 셋 다 **같은 모양**이라 한 벌(말 거는 화면)로 저작한다:
	 *    배경 + 그림 + 이름표 + 말풍선 + 버튼.
	 * ⚠️ 그 세 화면은 배경·그림을 비우면 **메인 화면 것을 쓴다** — 화면마다 같은 그림을 다시
	 *    올리지 않아도 된다(런타임 SpeechView 가 그렇게 눕힌다).
	 * ⚠️ 이 탭이 만지는 건 서버 SHELL 칸이다(메인 화면 = `assets`·`talkBoard`·`statBar`,
	 *    나머지 = `screens`). [디자인] 탭(로고·테마)과 **같은 칸**을 쓰므로 emit 할 때 원본을
	 *    먼저 펼쳐 서로의 값을 지우지 않는다.
	 */
	import { createEventDispatcher } from "svelte";
	import { Section, Input, Textarea, Select, Button, Label, Switch } from "../../lib/ui";
	import ImagePicker from "../chat-builder/ImagePicker.svelte";
	import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
	import { MAIN_SLOTS, STATBAR_SLOTS, tokenListOf, unknownProfileTokens  } from "./launcherSlots.js";

	export let bot;
	/** 서버 SHELL 칸 전체. 메인 화면(`assets`·`talkBoard`·`statBar`)과 `screens` 를 건드린다. */
	export let shell = {};
	/** assetId → URL. 말 거는 화면들의 썸네일용(저장하지 않는다). */
	export let screenUrls = {};
	/**
	 * 메인 화면 그림의 **슬롯키 → URL**. 썸네일용(저장하지 않는다).
	 * ⚠️ `screenUrls`(assetId 키)와 **키 체계가 다르다** — 셸 자산은 서버가 슬롯키로 풀어 내려준다.
	 */
	export let urls = {};
	/** [신원] 탭이 정의한 받을 정보 칸들 — 온보딩에서 그중 하나를 물어볼 수 있다. */
	export let profileFields = [];
	/** 지금 프리뷰에 떠 있는 화면(호출부가 내려준다). 어디를 보고 있는지 테두리로 보여주려고. */
	export let activeKey = "";

	/**
	 * 온보딩 한 장에서 받을 수 있는 것.
	 * ⭐ **닉네임은 전용 칸**이다(표시용·중복 허용). 개인정보(PROFILE)와 저장 위치도 성격도 달라서
	 *    — 전자는 랭킹·인사말에 쓰고, 후자는 수집 고지·파기 대상이다 — 따로 둔다.
	 * ⚠️ 한 장에 **하나만** 받는다. 여러 개가 필요하면 장을 나눈다(마스코트가 말을 거는 화면이라
	 *    한 번에 하나씩 묻는 게 그 은유에 맞다).
	 */
	$: inputChoices = [
		{ value: "", label: "— 받지 않음 —" },
		// ⚠️⚠️ 예전엔 여기 `{ value: "nickname", label: "닉네임 (표시용 이름)" }` 를 **따로** 박아
		//    뒀다. 개인정보 칸에도 `nickname` 이 있으면 `value` 가 겹쳐 Svelte 의 keyed each 가
		//    **중복 키로 죽었고**, 화면에서는 "선택이 안 된다"로만 보였다. 더 나쁜 건 저장 자리가
		//    갈려서(전용 칸 vs PROFILE) "온보딩에서 받은 값이 수정 화면에 안 나온다"가 된 것이다.
		// → 닉네임도 **[신원] 탭에서 만드는 보통 칸**이다. 목록은 선언된 칸 하나에서만 나온다.
		...(profileFields || [])
			.filter((f) => f?.key)
			.map((f) => ({ value: f.key, label: f.label || f.key })),
	];

	/**
	 * ⚠️⚠️ 온보딩이 **없는 칸**을 가리키고 있나.
	 *
	 * 이게 조용히 깨진다: 서버는 **선언된 키만** 통과시키므로(saveProfile), 가리키는 칸이
	 * 사라지면 사용자가 입력해도 **아무 데도 안 저장된다.** 에러도 안 난다 — 그래서
	 * "온보딩에서 넣었는데 수정 화면에 없다"로만 보인다. 저작 설정에서 잡을 수 있는 일이다.
	 * ⚠️ 칸 키를 고치거나 칸을 지울 때 온보딩이 남는 경로가 흔하다(실제로 `nickname2` 가 그랬다).
	 */
	$: obOrphanFields = [
		...new Set(
			(screens.onboarding || [])
				.map((sc) => sc?.input?.field)
				.filter((f) => f && !(profileFields || []).some((x) => x?.key === f)),
		),
	];
	/**
	 * 저작 문구에 쓸 수 있는 토큰 안내.
	 * ⭐ **선언된 칸을 그대로 읽어** 보여준다 — 목록을 손으로 적으면 저작자가 칸 이름을 바꿀 때
	 *    안내만 옛 이름으로 남는다. `{profile.nickname}` 이라 쓰고 실제 키는 `nickname2` 인
	 *    실수가 바로 이 자리에서 난다.
	 */
	$: tokenList = tokenListOf(profileFields);
	/** 온보딩 N 장의 글에서 선언 안 된 `{profile.X}` 찾기. */
	$: obBadTokens = unknownProfileTokens(
		(screens.onboarding || []).flatMap((sc) => [
			sc?.text,
			sc?.nameBadge,
			sc?.confirmLabel,
			sc?.input?.placeholder,
		]),
		profileFields,
	);
	/** 메인 말풍선(첫 멘트 + ▼ 로 넘겨 보는 추가 멘트)에서 같은 점검. */
	$: talkBadTokens = unknownProfileTokens(
		[main.talkBoard?.text, ...(main.talkBoard?.more || [])],
		profileFields,
	);
	/** 말 거는 화면 한 장(로그인·개인정보 안내)의 글에서 같은 점검. */
	$: badTokensOf = (key) =>
		unknownProfileTokens(
			[screens[key]?.text, screens[key]?.nameBadge, screens[key]?.confirmLabel],
			profileFields,
		);

	const setInput = (i, patch) => {
		const cur = screens.onboarding[i]?.input || {};
		setOb(i, { input: { ...cur, ...patch } });
	};

	const dispatch = createEventDispatcher();

	// ⚠️⚠️ **원본을 먼저 펼치고 정규화를 뒤에 둔다.** 반대로 하면(스프레드가 마지막) 저장된 값의
	//    null·비배열이 기본값을 **덮어써** `{#each screens.onboarding}` 과 `screens[key].xxx` 가
	//    터지고, 그러면 섹션이 통째로 안 그려진다("배경 칸이 없어졌다"로 보인다).
	// ⚠️ 그래도 원본을 펼치는 이유: 이 탭이 모르는 키(후속 추가분)를 조용히 지우지 않으려고.
	$: screens = {
		...(shell?.screens || {}),
		onboarding: Array.isArray(shell?.screens?.onboarding) ? shell.screens.onboarding : [],
		login: shell?.screens?.login || {},
		profileIntro: shell?.screens?.profileIntro || {},
	};

	// ⚠️ 원본(shell)을 먼저 펼친다 — [디자인] 탭이 쓰는 로고(assets.logo)를 조용히 지우지 않게.
	const emit = (next) => dispatch("change", { ...(shell || {}), screens: { ...screens, ...next } });

	/* ── 메인(홈) 화면 ──────────────────────────────────────────────────────
	   ⚠️ `screens` 안이 아니라 SHELL 칸 **바로 밑**에 산다(런타임 셸이 그 자리를 읽는다).
	      그래서 emit 도 따로 둔다 — `emit` 은 screens 로 감싸 버린다. */
	// 서버는 **저작자가 정한 값만** 내려준다 → 화면에 그릴 기본값은 여기서 채운다.
	// ⚠️ 기본값은 런타임 LAUNCHER/src/config.ts DEFAULT_SHELL 과 같아야 한다 — 두 벌이 되면
	//    "빌더에 보이는 것과 실제가 다르다"가 된다.
	$: main = {
		assets: { ...(shell?.assets || {}) },
		statBar: {
			showCurrency: shell?.statBar?.showCurrency ?? true,
			showSettings: shell?.statBar?.showSettings ?? true,
			...(shell?.statBar || {}),
		},
		talkBoard: {
			on: shell?.talkBoard?.on ?? true,
			text: shell?.talkBoard?.text ?? "",
			...(shell?.talkBoard || {}),
			// ⚠️ 스프레드 **뒤**에 둔다 — 저장값이 null·비배열이면 {#each} 가 터져서 섹션이 통째로
			//    안 그려진다(위 screens 와 같은 함정).
			more: Array.isArray(shell?.talkBoard?.more) ? shell.talkBoard.more : [],
		},
	};
	const emitMain = (patch) => dispatch("change", { ...(shell || {}), ...patch });
	const setStatBar = (key, value) => emitMain({ statBar: { ...main.statBar, [key]: value } });
	const setTalk = (key, value) => emitMain({ talkBoard: { ...main.talkBoard, [key]: value } });
	/* ── 추가 멘트(▼ 로 넘겨 보는 것) ──────────────────────────────────────
	   ⚠️ 첫 멘트는 `text` 에 그대로 둔다 — 배열 하나로 합치면 이미 저작된 말풍선을 옮기는
	      마이그레이션이 생긴다. 런타임이 `[text, ...more]` 로 합쳐서 본다. */
	const setMore = (i, v) => setTalk("more", main.talkBoard.more.map((m, n) => (n === i ? v : m)));
	const addMore = () => setTalk("more", [...main.talkBoard.more, ""]);
	const delMore = (i) => setTalk("more", main.talkBoard.more.filter((_, n) => n !== i));

	/**
	 * ⭐ "지금 이 화면을 만지는 중" 을 위로 알린다 → 우측 프리뷰가 그 화면으로 바뀐다.
	 *
	 * ⚠️ `click` 만으로는 부족하다 — 탭 키로 입력칸을 옮겨 다닐 때도 따라와야 한다.
	 *    그래서 `focusin`(버블링되는 focus)도 함께 듣는다.
	 * ⚠️ 같은 값이면 호출부가 걸러낸다(setPreviewFocus) — 글자 하나 칠 때마다 메시지가 쏟아지지 않게.
	 */
	const focusScreen = (kind, index) => dispatch("focus", { kind, index });

	const setOne = (key, patch) => emit({ [key]: { ...screens[key], ...patch } });
	const setOb = (i, patch) =>
		emit({ onboarding: screens.onboarding.map((s, n) => (n === i ? { ...s, ...patch } : s)) });
	const addOb = () => emit({ onboarding: [...screens.onboarding, {}] });
	const delOb = (i) => emit({ onboarding: screens.onboarding.filter((_, n) => n !== i) });
	const moveOb = (i, d) => {
		const to = i + d;
		if (to < 0 || to >= screens.onboarding.length) return;
		const next = screens.onboarding.slice();
		const [x] = next.splice(i, 1);
		next.splice(to, 0, x);
		emit({ onboarding: next });
	};

	/* ── 그림 고르기 ───────────────────────────────────────────────────── */
	let pickerOpen = false;
	// { where:'main'|'login'|'profileIntro'|'ob', i?, field: 슬롯키 | 'backgroundAssetId'|'imageAssetId'|'titleAssetId' }
	// ⚠️ where='main' 일 때 field 는 **셸 슬롯키**(background·mascot)다 — 다른 화면과 저장 자리도
	//    썸네일 키 체계도 다르다(슬롯키 vs assetId).
	let picking = null;

	const openPicker = (where, field, i = null) => {
		picking = { where, field, i };
		pickerOpen = true;
	};
	// 지금 고르는 칸에 이미 들어 있는 자산(모달에서 선택 표시용).
	$: pickerAssetId = !picking
		? null
		: picking.where === "main"
			? main.assets[picking.field] || null
			: picking.where === "ob"
				? screens.onboarding[picking.i]?.[picking.field] || null
				: screens[picking.where]?.[picking.field] || null;

	const onPick = (e) => {
		if (!picking) return;
		const { assetId, url } = e.detail || {};
		// 메인 화면 그림은 **슬롯키**로 썸네일을 들고, 저장은 assetId 로 한다.
		if (picking.where === "main") {
			urls = { ...urls, [picking.field]: url };
			emitMain({ assets: { ...main.assets, [picking.field]: assetId } });
			picking = null;
			return;
		}
		screenUrls = { ...screenUrls, [assetId]: url };
		if (picking.where === "ob") setOb(picking.i, { [picking.field]: assetId });
		else setOne(picking.where, { [picking.field]: assetId });
		picking = null;
	};
	const clear = (where, field, i = null) => {
		// ⚠️ 메인 화면 슬롯은 **키를 지운다**(null 을 넣으면 서버가 "그 슬롯에 자산이 있다"로 보고
		//    resolve 를 시도한다).
		if (where === "main") {
			const assets = { ...main.assets };
			delete assets[field];
			const next = { ...urls };
			delete next[field];
			urls = next;
			emitMain({ assets });
			return;
		}
		if (where === "ob") setOb(i, { [field]: null });
		else setOne(where, { [field]: null });
	};
</script>

<div class="tw-flex tw-flex-col tw-gap-6">
	<!-- ⭐ 메인(홈) 화면 — 맨 앞이 제자리다. 들어오면 가장 먼저 보이는 화면이고, 다른 화면들이
	     배경·캐릭터를 여기서 물려받는다. 클릭하면 프리뷰가 홈으로 돌아온다(activeKey === ''). -->
	<Section title="메인 화면" description="런처에 들어오면 보이는 화면입니다.">
		<!-- svelte-ignore a11y-no-static-element-interactions -->
		<div
			class="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-p-2 tw-transition-colors {activeKey === ''
				? 'tw-bg-primary/5 tw-ring-1 tw-ring-primary'
				: ''}"
			on:click={() => focusScreen("home")}
			on:focusin={() => focusScreen("home")}
		>
			{#each MAIN_SLOTS as slot (slot.key)}
				<div class="tw-flex tw-items-start tw-gap-3">
					<button
						type="button"
						class="slot-thumb tw-shrink-0"
						on:click={() => openPicker("main", slot.key)}
						title="{slot.label} 고르기"
					>
						{#if urls[slot.key]}
							<img src={urls[slot.key]} alt="" />
						{:else}
							<span class="tw-text-[11px] tw-text-muted-foreground">고르기</span>
						{/if}
					</button>
					<div class="tw-min-w-0 tw-flex-1">
						<div class="tw-text-sm tw-font-medium">{slot.label}</div>
						<p class="tw-mt-0.5 tw-text-xs tw-text-muted-foreground">{slot.hint}</p>
						{#if main.assets[slot.key]}
							<button
								type="button"
								class="tw-mt-1 tw-text-xs tw-text-muted-foreground tw-underline"
								on:click={() => clear("main", slot.key)}>비우기</button
							>
						{/if}
					</div>
				</div>
			{/each}

			<div class="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-border tw-pt-4">
				<Label class="tw-text-sm">말풍선</Label>
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
					<Switch
						checked={main.talkBoard.on}
						on:click={() => setTalk("on", !main.talkBoard.on)}
						aria-label="말풍선 보이기"
					/>
					말풍선 보이기
				</div>
				<Textarea
					rows="3"
					placeholder="안녕! 오늘은 {'{'}date{'}'}이야. {'{'}nickname{'}'}의 행운을 빌어줄게!"
					value={main.talkBoard.text}
					on:input={(e) => setTalk("text", e.target.value)}
				/>
				<!-- ⭐ 치환 토큰을 저작 문구가 쓰게 한 이유: 날짜·이름을 코드가 아니라 저작이 정해야
				     도메인 중립이 된다(설계 §0-1).
				     ⚠️⚠️ 목록을 **손으로 적지 않는다.** 여기만 `{date}`·`{name}` 을 박아 뒀더니
				        ① [신원] 탭에서 만든 칸들이 안내에 안 나오고(말풍선에서도 멀쩡히 도는데)
				        ② 권장 표기를 `{nickname}` 으로 바꿨을 때 **여기만 옛 이름으로 남았다.**
				        다른 섹션처럼 tokenList 를 쓴다. -->
				<p class="tw-text-xs tw-text-muted-foreground">
					문구에 쓸 수 있는 값:
					{#each tokenList as tk (tk)}<code class="tw-mx-0.5">{tk}</code>{/each}
					— 이름은 로그인 전까지 "너"로 나옵니다.
				</p>
				{#if talkBadTokens.length > 0}
					<p class="tw-text-xs tw-text-destructive">
						⚠️ 없는 칸을 가리키는 값이 있습니다(<!--
						-->{#each talkBadTokens as k, i (k)}<code>{"{profile." + k + "}"}</code>{#if i < talkBadTokens.length - 1}, {/if}{/each})
						— 화면에서 <strong>빈칸으로 사라집니다.</strong>
					</p>
				{/if}

				<!-- ⭐ 추가 멘트 — 말풍선 안 ▼ 로 넘겨 본다(시안 "메인화면"의 18×18 삼각형).
				     ⚠️ 멘트가 **하나뿐이면 ▼ 가 안 나온다** — 눌러도 아무 일 없는 버튼을 두지 않는다.
				        그래서 '추가' 를 눌러야 비로소 생기는 게 맞다. -->
				<div class="tw-flex tw-flex-col tw-gap-2 tw-pt-1">
					<div class="tw-flex tw-items-center tw-justify-between">
						<Label class="tw-text-sm">다른 이야기</Label>
						<Button variant="outline" size="sm" on:click={addMore}>+ 멘트 추가</Button>
					</div>
					{#if main.talkBoard.more.length === 0}
						<p class="tw-text-xs tw-text-muted-foreground">
							멘트를 더 넣으면 말풍선 안에 ▼ 가 생기고, 누를 때마다 다른 이야기가 나옵니다.
						</p>
					{:else}
						{#each main.talkBoard.more as m, i}
							<div class="tw-flex tw-items-start tw-gap-2">
								<Textarea
									rows="2"
									class="tw-flex-1"
									placeholder="오늘은 무슨 빵을 구울까?"
									value={m}
									on:input={(e) => setMore(i, e.target.value)}
								/>
								<button
									type="button"
									class="tw-mt-2 tw-text-xs tw-text-muted-foreground tw-underline"
									on:click={() => delMore(i)}>삭제</button
								>
							</div>
						{/each}
					{/if}
				</div>
			</div>

			<div class="tw-flex tw-flex-col tw-gap-2 tw-border-t tw-border-border tw-pt-4">
				<Label class="tw-text-sm">상단 바</Label>
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
					<Switch
						checked={main.statBar.showCurrency}
						on:click={() => setStatBar("showCurrency", !main.statBar.showCurrency)}
						aria-label="보유 재화"
					/>
					보유 재화
				</div>
				<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
					<Switch
						checked={main.statBar.showSettings}
						on:click={() => setStatBar("showSettings", !main.statBar.showSettings)}
						aria-label="설정 버튼"
					/>
					설정 버튼
				</div>
				<!-- ⭐ 설정 아이콘은 **보이기 스위치 바로 밑**이 제자리다. 위 메인 화면 그림 묶음
				     (MAIN_SLOTS)에 끼우면 배경·마스코트와 한 줄로 서서 어느 게 상단 바인지 안 보인다.
				     ⚠️ 저장 자리는 그 둘과 **같은 칸**(`shell.assets`)이라 picker 의 where 는 'main'
				        그대로다 — 썸네일 키도 슬롯키 체계가 같다(urls[slot.key]). -->
				{#each STATBAR_SLOTS as slot (slot.key)}
					<div
						class="tw-flex tw-items-start tw-gap-3 {main.statBar.showSettings
							? ''
							: 'tw-opacity-50'}"
					>
						<button
							type="button"
							class="slot-thumb tw-shrink-0"
							on:click={() => openPicker("main", slot.key)}
							title="{slot.label} 고르기"
						>
							{#if urls[slot.key]}
								<img src={urls[slot.key]} alt="" />
							{:else}
								<span class="tw-text-[11px] tw-text-muted-foreground">고르기</span>
							{/if}
						</button>
						<div class="tw-min-w-0 tw-flex-1">
							<div class="tw-text-sm tw-font-medium">{slot.label}</div>
							<p class="tw-mt-0.5 tw-text-xs tw-text-muted-foreground">{slot.hint}</p>
							{#if main.assets[slot.key]}
								<button
									type="button"
									class="tw-mt-1 tw-text-xs tw-text-muted-foreground tw-underline"
									on:click={() => clear("main", slot.key)}>비우기</button
								>
							{/if}
						</div>
					</div>
				{/each}
			</div>
		</div>
		<p class="tw-mt-2 tw-text-xs tw-text-muted-foreground">
			아래 큰 버튼·오른쪽 아이콘은 <strong>[타일] 탭</strong>에서, 색과 글꼴은 <strong>[디자인] 탭</strong>에서
			정합니다.
		</p>
	</Section>

	<Section
		title="온보딩"
		description="처음 들어온 사람에게 순서대로 보여줄 장면입니다. 한 번 보면 다시 안 나옵니다."
	>
		<div class="tw-flex tw-flex-col tw-gap-3">
			{#if screens.onboarding.length === 0}
				<p class="tw-py-4 tw-text-center tw-text-sm tw-text-muted-foreground">
					온보딩이 없습니다. 안 만들어도 됩니다 — 바로 홈으로 들어갑니다.
				</p>
			{/if}
			{#each screens.onboarding as sc, i}
				<!-- svelte-ignore a11y-no-static-element-interactions -->
				<div
					class="tw-rounded-lg tw-border tw-p-3 tw-transition-colors {activeKey === `ob:${i}`
						? 'tw-border-primary tw-bg-primary/5'
						: 'tw-border-border'}"
					on:click={() => focusScreen("onboarding", i)}
					on:focusin={() => focusScreen("onboarding", i)}
				>
					<div class="tw-mb-2 tw-flex tw-items-center tw-gap-2">
						<span class="tw-text-xs tw-font-semibold">{i + 1}장</span>
						<div class="tw-ml-auto tw-flex tw-items-center tw-gap-1">
							<button type="button" class="tw-px-1.5 tw-text-xs tw-text-muted-foreground disabled:tw-opacity-30" disabled={i === 0} on:click={() => moveOb(i, -1)}>▲</button>
							<button type="button" class="tw-px-1.5 tw-text-xs tw-text-muted-foreground disabled:tw-opacity-30" disabled={i === screens.onboarding.length - 1} on:click={() => moveOb(i, 1)}>▼</button>
							<button type="button" class="tw-px-1 tw-text-xs tw-text-destructive" on:click={() => delOb(i)}>✕</button>
						</div>
					</div>
					<div class="tw-flex tw-gap-3">
						<div class="tw-flex tw-shrink-0 tw-flex-col tw-gap-3">
							<div class="sc-slot">
								<button type="button" class="sc-thumb" on:click={() => openPicker("ob", "imageAssetId", i)}>
									{#if screenUrls[sc.imageAssetId]}<img src={screenUrls[sc.imageAssetId]} alt="" />{:else}<span>+</span>{/if}
								</button>
								<span class="sc-cap">캐릭터</span>
								{#if sc.imageAssetId}
									<button type="button" class="sc-clear" on:click={() => clear("ob", "imageAssetId", i)}>비우기</button>
								{/if}
							</div>
							<div class="sc-slot">
								<button type="button" class="sc-thumb sc-bg" on:click={() => openPicker("ob", "backgroundAssetId", i)}>
									{#if screenUrls[sc.backgroundAssetId]}<img src={screenUrls[sc.backgroundAssetId]} alt="" />{:else}<span>+</span>{/if}
								</button>
								<span class="sc-cap">배경</span>
								{#if sc.backgroundAssetId}
									<button type="button" class="sc-clear" on:click={() => clear("ob", "backgroundAssetId", i)}>비우기</button>
								{/if}
							</div>
							<div class="sc-slot">
								<button type="button" class="sc-thumb sc-title" on:click={() => openPicker("ob", "titleAssetId", i)}>
									{#if screenUrls[sc.titleAssetId]}<img src={screenUrls[sc.titleAssetId]} alt="" />{:else}<span>+</span>{/if}
								</button>
								<span class="sc-cap">타이틀</span>
								{#if sc.titleAssetId}
									<button type="button" class="sc-clear" on:click={() => clear("ob", "titleAssetId", i)}>비우기</button>
								{/if}
							</div>
						</div>
						<div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
							<Input placeholder="이름표 (망소금)" value={sc.nameBadge || ""} on:input={(e) => setOb(i, { nameBadge: e.target.value })} />
							<Textarea rows="3" placeholder="안녕! 나는 망소금이라고 해." value={sc.text || ""} on:input={(e) => setOb(i, { text: e.target.value })} />
							<Input placeholder="버튼 문구 (비우면 '다음' / 마지막 장은 '시작하기')" value={sc.confirmLabel || ""} on:input={(e) => setOb(i, { confirmLabel: e.target.value })} />

							<!-- 이 장에서 받을 입력 한 칸(선택). -->
							<div class="tw-flex tw-items-center tw-gap-2">
								<Label class="tw-shrink-0 tw-text-xs tw-text-muted-foreground">물어보기</Label>
								<Select
									class="tw-min-w-0 tw-flex-1"
									value={sc.input?.field || ""}
									on:change={(e) =>
										setOb(i, e.target.value ? { input: { ...(sc.input || {}), field: e.target.value } } : { input: null })}
								>
									{#each inputChoices as c (c.value)}
										<option value={c.value}>{c.label}</option>
									{/each}
								</Select>
							</div>
							{#if sc.input?.field}
								<div class="tw-flex tw-items-center tw-gap-2 tw-pl-[60px]">
									<Input
										class="tw-min-w-0 tw-flex-1"
										placeholder="입력칸 안내문 (예: 뭐라고 부를까?)"
										value={sc.input?.placeholder || ""}
										on:input={(e) => setInput(i, { placeholder: e.target.value })}
									/>
									<div class="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-text-xs">
										<Switch
											checked={!!sc.input?.required}
											on:click={() => setInput(i, { required: !sc.input?.required })}
											aria-label="필수"
										/>
										필수
									</div>
								</div>
								{#if sc.input.field === "nickname"}
									<p class="tw-pl-[60px] tw-text-xs tw-text-muted-foreground">
										소셜 계정의 이름이 <strong>미리 채워집니다</strong>. 비워 두고 넘어가면 기존 이름이 유지됩니다.
									</p>
								{/if}
							{/if}
						</div>
					</div>
				</div>
			{/each}
			<div><Button variant="outline" size="sm" on:click={addOb}>+ 장면 추가</Button></div>
			<p class="tw-text-xs tw-text-muted-foreground">
				⚠️ 온보딩은 <strong>로그인한 뒤</strong> 한 번 나옵니다(계정에 기록하므로 기기를 바꿔도 다시 안 뜹니다).
			</p>
			<!-- ⭐ 앞 장에서 받은 값을 뒷 장에서 부를 수 있다 — 저장이 장을 넘길 때 일어난다. -->
			<p class="tw-text-xs tw-text-muted-foreground">
				문구에 쓸 수 있는 값:
				{#each tokenList as tk (tk)}<code class="tw-mx-0.5">{tk}</code>{/each}
				— 앞 장에서 받은 값은 <strong>뒷 장에서 바로</strong> 쓸 수 있습니다.
			</p>
			{#if obOrphanFields.length > 0}
				<p class="tw-text-xs tw-text-destructive">
					⚠️ 온보딩이 <strong>없는 칸</strong>을 가리킵니다(<!--
					-->{#each obOrphanFields as k, i (k)}<code>{k}</code>{#if i < obOrphanFields.length - 1}, {/if}{/each})
					— 사용자가 입력해도 <strong>저장되지 않습니다.</strong> [신원] 탭에서 그 칸을 만들거나,
					온보딩의 "받을 정보"를 다시 고르세요.
				</p>
			{/if}
			{#if obBadTokens.length > 0}
				<p class="tw-text-xs tw-text-destructive">
					⚠️ 없는 칸을 가리키는 값이 있습니다(<!--
					-->{#each obBadTokens as k, i (k)}<code>{"{profile." + k + "}"}</code>{#if i < obBadTokens.length - 1}, {/if}{/each})
					— 화면에서 <strong>빈칸으로 사라집니다.</strong> [신원] 탭의 칸 키와 똑같이 적어주세요.
				</p>
			{/if}
		</div>
	</Section>

	{#each [["login", "로그인 화면", "소셜 로그인 버튼 위에 보일 그림과 문구입니다. 로고는 [디자인] 탭의 로고를 씁니다."], ["profileIntro", "개인정보 안내", "정보를 처음 받기 전에 보여줄 안내입니다. 비우면 바로 입력 화면으로 갑니다."]] as [key, title, desc]}
		<Section {title} description={desc}>
			{#if key === "profileIntro"}
				<!-- ⭐ 안내 화면 스위치. **없으면 켜짐** — 끄면 on:false 만 저장한다(켜면 키를 지운다).
				     끄면 필수 정보로 막혔을 때 이 화면을 건너뛰고 바로 입력 화면이다(런타임 ProfileScreen hasIntro).
				     아래 문구·그림은 그대로 남는다(다시 켜면 돌아온다). -->
				<div class="tw-mb-2 tw-flex tw-flex-col tw-gap-1">
					<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
						<Switch
							checked={screens.profileIntro.on !== false}
							on:click={() => {
								setOne("profileIntro", { on: screens.profileIntro.on === false ? undefined : false });
								focusScreen("profileIntro");
							}}
							aria-label="안내 화면 보이기"
						/>
						안내 화면 보이기
					</div>
					<p class="tw-text-xs tw-text-muted-foreground">
						끄면 이 안내 없이 <strong>바로 정보 입력 화면</strong>으로 갑니다. 아래 문구·그림은 그대로 남습니다.
					</p>
				</div>
			{/if}
			<!-- svelte-ignore a11y-no-static-element-interactions -->
			<div
				class="tw-flex tw-gap-3 tw-rounded-lg tw-p-2 tw-transition-colors {activeKey === key
					? 'tw-bg-primary/5 tw-ring-1 tw-ring-primary'
					: ''}"
				on:click={() => focusScreen(key)}
				on:focusin={() => focusScreen(key)}
			>
				<div class="tw-flex tw-shrink-0 tw-flex-col tw-gap-3">
					<div class="sc-slot">
						<button type="button" class="sc-thumb" on:click={() => openPicker(key, "imageAssetId")}>
							{#if screenUrls[screens[key].imageAssetId]}<img src={screenUrls[screens[key].imageAssetId]} alt="" />{:else}<span>+</span>{/if}
						</button>
						<span class="sc-cap">캐릭터</span>
						{#if screens[key].imageAssetId}
							<button type="button" class="sc-clear" on:click={() => clear(key, "imageAssetId")}>비우기</button>
						{/if}
					</div>
					<div class="sc-slot">
						<button type="button" class="sc-thumb sc-bg" on:click={() => openPicker(key, "backgroundAssetId")}>
							{#if screenUrls[screens[key].backgroundAssetId]}<img src={screenUrls[screens[key].backgroundAssetId]} alt="" />{:else}<span>+</span>{/if}
						</button>
						<span class="sc-cap">배경</span>
						{#if screens[key].backgroundAssetId}
							<button type="button" class="sc-clear" on:click={() => clear(key, "backgroundAssetId")}>비우기</button>
						{/if}
					</div>
					<div class="sc-slot">
						<button type="button" class="sc-thumb sc-title" on:click={() => openPicker(key, "titleAssetId")}>
							{#if screenUrls[screens[key].titleAssetId]}<img src={screenUrls[screens[key].titleAssetId]} alt="" />{:else}<span>+</span>{/if}
						</button>
						<span class="sc-cap">타이틀</span>
						{#if screens[key].titleAssetId}
							<button type="button" class="sc-clear" on:click={() => clear(key, "titleAssetId")}>비우기</button>
						{/if}
					</div>
				</div>
				<div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
					{#if key !== "login"}
						<Input placeholder="이름표 (망소금)" value={screens[key].nameBadge || ""} on:input={(e) => setOne(key, { nameBadge: e.target.value })} />
					{/if}
					<Textarea rows="3" placeholder={key === "login" ? "시작하려면 로그인해줘!" : "행운빵을 구우려면 네 정보가 조금 필요해."} value={screens[key].text || ""} on:input={(e) => setOne(key, { text: e.target.value })} />
					{#if key !== "login"}
						<Input placeholder="버튼 문구 (비우면 '입력하러 가기')" value={screens[key].confirmLabel || ""} on:input={(e) => setOne(key, { confirmLabel: e.target.value })} />
					{/if}
				</div>
			</div>
			<p class="tw-mt-2 tw-text-xs tw-text-muted-foreground">
				배경·그림을 비우면 <strong>메인 화면의 배경·마스코트</strong>를 그대로 씁니다.
			</p>
			<p class="tw-text-xs tw-text-muted-foreground">
				문구에 쓸 수 있는 값:
				{#each tokenList as tk (tk)}<code class="tw-mx-0.5">{tk}</code>{/each}
			</p>
			{#if key === "profileIntro"}
				<!-- ⚠️⚠️ 이 화면은 "필수 정보가 비어서" 뜬다 — 그 비어 있는 칸을 가리키면 당연히 빈칸이다. -->
				<p class="tw-text-xs tw-text-muted-foreground">
					⚠️ 이 화면은 <strong>필수 정보가 비어 있을 때</strong> 뜹니다(어느 정보를 필수로 할지는
					[타일] 탭의 <strong>누를 수 있는 조건</strong>에서 켭니다). 그래서
					<strong>아직 안 받은 칸</strong>을 문구에 쓰면 빈칸으로 보입니다 — 온보딩에서 이미 받은
					칸을 가리켜주세요.
				</p>
			{/if}
			{#if badTokensOf(key).length > 0}
				<p class="tw-text-xs tw-text-destructive">
					⚠️ 없는 칸을 가리키는 값이 있습니다(<!--
					-->{#each badTokensOf(key) as k, i (k)}<code>{"{profile." + k + "}"}</code>{#if i < badTokensOf(key).length - 1}, {/if}{/each})
					— 화면에서 <strong>빈칸으로 사라집니다.</strong> [신원] 탭의 칸 키와 똑같이 적어주세요.
				</p>
			{/if}
		</Section>
	{/each}
</div>

<ImagePicker
	bind:open={pickerOpen}
	chatId={bot.id}
	contextFolder={botAssetFolder(bot)}
	mode="single"
	currentAssetId={pickerAssetId}
	on:select={onPick}
/>

<style>
	/* 메인 화면 그림 슬롯. 투명 PNG 가 많아 체크무늬 바탕에 올려야 가장자리가 보인다.
	   (말 거는 화면의 .sc-thumb 보다 크다 — 배경·마스코트는 화면을 채우는 그림이다) */
	.slot-thumb {
		width: 88px;
		height: 88px;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		border-radius: 10px;
		border: 1px dashed hsl(var(--sb-border));
		background-color: hsl(var(--sb-muted));
		background-image:
			linear-gradient(45deg, rgba(0, 0, 0, 0.06) 25%, transparent 25%),
			linear-gradient(-45deg, rgba(0, 0, 0, 0.06) 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, rgba(0, 0, 0, 0.06) 75%),
			linear-gradient(-45deg, transparent 75%, rgba(0, 0, 0, 0.06) 75%);
		background-size: 12px 12px;
		background-position: 0 0, 0 6px, 6px -6px, -6px 0;
		cursor: pointer;
	}
	.slot-thumb img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}

	.sc-thumb {
		width: 72px; height: 72px;
		display: flex; align-items: center; justify-content: center;
		overflow: hidden; border-radius: 10px;
		border: 1px dashed hsl(var(--sb-border));
		background: hsl(var(--sb-muted));
		cursor: pointer; font-size: 10px; color: hsl(var(--sb-muted-foreground));
	}
	.sc-thumb img { max-width: 100%; max-height: 100%; object-fit: contain; }
	/* 두 칸이 똑같이 생기면 어느 쪽이 배경인지 알 수 없다 → 라벨을 **보이게** 둔다. */
	.sc-slot { display: flex; flex-direction: column; align-items: center; gap: 2px; width: 72px; }
	.sc-cap { font-size: 11px; color: hsl(var(--sb-muted-foreground)); }
	.sc-clear {
		font-size: 10px;
		color: hsl(var(--sb-muted-foreground));
		text-decoration: underline;
		background: none; border: 0; padding: 0; cursor: pointer;
	}
	/* 배경은 꽉 채워 보여야 감이 온다(그림은 투명 PNG 가 많아 contain). */
	.sc-bg img { width: 100%; height: 100%; object-fit: cover; }
	/* 타이틀은 가로로 긴 글자 그림이 많아 가로로 꽉 채워 보여 준다. */
	.sc-title { height: 48px; }
</style>
